import { retry, tryCatch } from '@wix/funnel-utils'
import { CancelTokenSource } from '@wix/http-client'

import { ambassadorHttpClient } from '../../../../api/httpClient'
import { BusinessTermService } from '../../../reusable/Category/api/BusinessTermService'
import type { StateCategory } from '../../../reusable/Category/state/types'

const cache = new Map<string, [StateCategory['businessTerm'], StateCategory['businessSegmentId']]>()

// This is without debounce, as this happens on click on chips
export async function loadCategory(
  query: string,
  language: string,
  cancelToken: CancelTokenSource['token'],
  cb: (
    error: Error | undefined,
    businessTerm: StateCategory['businessTerm'],
    businessSegmentId: StateCategory['businessSegmentId'],
  ) => void,
) {
  // prevent unneccessary requests
  if (cache.has(query)) {
    const [businessTerm, businessSegmentId] = cache.get(query)!
    cb(undefined, businessTerm, businessSegmentId)
    return
  }

  const bts = new BusinessTermService(ambassadorHttpClient)
  const promise = retry(
    async ({ abort }) => {
      if (query.length > 100) {
        abort(new Error('query length should be less than 100'))
      }
      const terms = await bts.search({ query, limit: 1, language, cancelToken })
      const term = terms[0]
      if (!term) {
        abort(new Error('no term returned for given query'))
      }
      return term
    },
    { retries: 3, delayMs: 1000 },
  )
  const [error, businessTerm] = await tryCatch(() => promise)
  if (error) {
    cb(error, undefined, undefined)
    return
  }
  const { businessTermId } = businessTerm
  // is it fine if this request fails? should be fine
  const [bsError, businessSegmentId] = businessTermId
    ? await tryCatch(async () => {
        const res = await bts.getBusinessSegmentIdByTermId(businessTermId, cancelToken)
        cancelToken.throwIfRequested()
        return res
      })
    : [undefined, undefined]
  if (ambassadorHttpClient.isCancel(bsError)) {
    cb(bsError, undefined, undefined)
    return
  }

  cache.set(query, [businessTerm, businessSegmentId])
  cb(undefined, businessTerm, businessSegmentId)
}
